// StudyDost — Parent Portal Page
// frontend/src/pages/ParentPage.js
// Route: /parent

import React, { useState } from 'react';
import { BarChart2, BookOpen, Clock, TrendingUp, Shield, MessageCircle, Star, ChevronRight } from 'lucide-react';

const FEATURES = [
  {
    icon: <BarChart2 size={24} color="#1E40AF" />,
    title: 'Weekly Study Report',
    desc: 'Har Sunday aapke bachche ki study ka complete summary — kaun se subject mein strong, kaun mein weak.',
    bg: '#EFF6FF',
  },
  {
    icon: <BookOpen size={24} color="#16A34A" />,
    title: 'Subject-wise Analysis',
    desc: 'Math, Science, English — har subject mein kitne sawaal pooche, kahan help chahiye.',
    bg: '#F0FDF4',
  },
  {
    icon: <Clock size={24} color="#D97706" />,
    title: 'Study Time Tracking',
    desc: 'Bachcha kitne baje padhta hai, kitna time deta hai — daily pattern clearly dikhega.',
    bg: '#FFFBEB',
  },
  {
    icon: <TrendingUp size={24} color="#7C3AED" />,
    title: 'Progress Over Time',
    desc: 'Week-on-week improvement track karein. Streak, badges, aur achievement milenge.',
    bg: '#F5F3FF',
  },
];

const FAQS = [
  {
    q: 'Parent portal kab launch hoga?',
    a: 'Hum May 2026 mein parent portal launch kar rahe hain. Abhi apna number register karein aur aap pehle users mein se honge!',
  },
  {
    q: 'Kya yeh free hai?',
    a: 'Haan! Parent portal bilkul FREE hai. Bachche ke plan ke saath automatically milega.',
  },
  {
    q: 'Mujhe koi app download karni padegi?',
    a: 'Nahi! Weekly report seedha WhatsApp par aayegi. Aur web portal browser mein khulega — koi download nahi.',
  },
  {
    q: 'Ek parent kitne bachcho ko track kar sakta hai?',
    a: 'Aap multiple bachcho ko ek hi account se track kar sakte hain.',
  },
];

export default function ParentPage() {
  const [phone, setPhone] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleRegister = async () => {
    if (phone.length !== 10) return;
    // For now just show success — backend registration coming soon
    setSubmitted(true);
  };

  return (
    <div style={styles.page}>

      {/* ── Hero ── */}
      <div style={styles.hero}>
        <div style={styles.heroBadge}>
          <Shield size={14} color="#1E40AF" />
          <span>Parent Portal — Coming Soon</span>
        </div>
        <h1 style={styles.heroTitle}>
          Apne Bachche Ki Padhai<br />
          <span style={styles.heroAccent}>Ab Aapki Muthi Mein</span>
        </h1>
        <p style={styles.heroDesc}>
          StudyDost aapko har week aapke bachche ki complete study report bhejega — WhatsApp par. Bilkul FREE.
        </p>
        <div style={styles.statRow}>
          {[
            { num: '5+', label: 'Subjects tracked' },
            { num: 'Weekly', label: 'WhatsApp report' },
            { num: '100%', label: 'Free forever' },
          ].map(s => (
            <div key={s.label} style={styles.statBox}>
              <div style={styles.statNum}>{s.num}</div>
              <div style={styles.statLabel}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Register CTA ── */}
      <div style={styles.registerBox}>
        {!submitted ? (
          <>
            <h2 style={styles.registerTitle}>🔔 Early Access Register Karein</h2>
            <p style={styles.registerDesc}>
              Portal launch hone par sabse pehle notification paayein
            </p>
            <div style={styles.phoneRow}>
              <div style={styles.countryCode}>🇮🇳 +91</div>
              <input
                style={styles.phoneInput}
                type="tel"
                placeholder="Apna WhatsApp number"
                value={phone}
                onChange={e => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                inputMode="numeric"
              />
            </div>
            <button
              style={{
                ...styles.registerBtn,
                opacity: phone.length === 10 ? 1 : 0.5,
              }}
              onClick={handleRegister}
              disabled={phone.length !== 10}
            >
              Register Karein — FREE
              <ChevronRight size={18} />
            </button>
            <p style={styles.registerNote}>
              🔒 Spam nahi karenge. Sirf launch notification.
            </p>
          </>
        ) : (
          <div style={styles.successBox}>
            <div style={styles.successEmoji}>🎉</div>
            <h3 style={styles.successTitle}>Register ho gaye!</h3>
            <p style={styles.successDesc}>
              +91 {phone} par hum aapko launch ke time notify karenge.
            </p>
            <p style={{ fontSize: 14, color: '#6B7280', marginTop: 8 }}>
              Abhi ke liye, apne bachche ke number par <strong>PARENT</strong> type karke StudyDost ko bhejein — unhe weekly WhatsApp report milegi!
            </p>
          </div>
        )}
      </div>

      {/* ── Sample Report Preview ── */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>📊 Aisa Dikhega Weekly Report</h2>
        <div style={styles.reportPreview}>
          <div style={styles.reportHeader}>
            <span style={styles.reportLogo}>📚</span>
            <div>
              <div style={styles.reportName}>Ravi Kumar ka Report</div>
              <div style={styles.reportDate}>Week of 21 Apr 2026 • Class 10 CBSE</div>
            </div>
          </div>
          <div style={styles.reportDivider} />
          <div style={styles.reportStats}>
            {[
              { label: 'Total Questions', value: '23', icon: '❓', color: '#1E40AF' },
              { label: 'Streak', value: '6 days 🔥', icon: '📅', color: '#EA580C' },
              { label: 'Best Subject', value: 'Science', icon: '🔬', color: '#16A34A' },
              { label: 'Needs Help', value: 'Math', icon: '⚠️', color: '#D97706' },
            ].map(stat => (
              <div key={stat.label} style={styles.reportStat}>
                <div style={styles.reportStatIcon}>{stat.icon}</div>
                <div style={{ ...styles.reportStatValue, color: stat.color }}>{stat.value}</div>
                <div style={styles.reportStatLabel}>{stat.label}</div>
              </div>
            ))}
          </div>
          <div style={styles.reportDivider} />
          <div style={styles.subjectBars}>
            {[
              { subject: '🔬 Science', count: 12, max: 12, color: '#16A34A' },
              { subject: '📖 English', count: 5, max: 12, color: '#7C3AED' },
              { subject: '🧮 Math', count: 4, max: 12, color: '#1E40AF' },
              { subject: '🌍 SST', count: 2, max: 12, color: '#D97706' },
            ].map(s => (
              <div key={s.subject} style={styles.barRow}>
                <div style={styles.barLabel}>{s.subject}</div>
                <div style={styles.barTrack}>
                  <div style={{
                    ...styles.barFill,
                    width: `${(s.count / s.max) * 100}%`,
                    background: s.color,
                  }} />
                </div>
                <div style={styles.barCount}>{s.count}</div>
              </div>
            ))}
          </div>
          <div style={styles.reportFooter}>
            Powered by StudyDost • studydost.ai
          </div>
        </div>
      </div>

      {/* ── Features ── */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Aapko Kya Milega?</h2>
        <div style={styles.featuresGrid}>
          {FEATURES.map(f => (
            <div key={f.title} style={{ ...styles.featureCard, background: f.bg }}>
              <div style={styles.featureIcon}>{f.icon}</div>
              <div style={styles.featureTitle}>{f.title}</div>
              <div style={styles.featureDesc}>{f.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── How it works ── */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Kaise Kaam Karta Hai?</h2>
        <div style={styles.steps}>
          {[
            { num: '1', title: 'Bachcha StudyDost use karta hai', desc: 'WhatsApp ya web app par sawaal poochta hai' },
            { num: '2', title: 'System automatically track karta hai', desc: 'Subject, time, questions — sab record hota hai' },
            { num: '3', title: 'Aapko Sunday report aata hai', desc: 'WhatsApp par complete weekly summary milta hai' },
          ].map(step => (
            <div key={step.num} style={styles.step}>
              <div style={styles.stepNum}>{step.num}</div>
              <div>
                <div style={styles.stepTitle}>{step.title}</div>
                <div style={styles.stepDesc}>{step.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── FAQ ── */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Aksar Pooche Jaane Wale Sawaal</h2>
        {FAQS.map((faq, i) => (
          <div key={i} style={styles.faqCard}>
            <button
              style={styles.faqQuestion}
              onClick={() => setOpenFaq(openFaq === i ? null : i)}
            >
              <span>{faq.q}</span>
              <ChevronRight
                size={16}
                style={{ transform: openFaq === i ? 'rotate(90deg)' : 'none', transition: '0.2s' }}
              />
            </button>
            {openFaq === i && (
              <div style={styles.faqAnswer}>{faq.a}</div>
            )}
          </div>
        ))}
      </div>

      {/* ── Footer CTA ── */}
      <div style={styles.footerCta}>
        <div style={styles.footerCtaEmoji}>📱</div>
        <h3 style={styles.footerCtaTitle}>Abhi StudyDost Try Karein</h3>
        <p style={styles.footerCtaDesc}>
          Apne bachche ko WhatsApp par StudyDost se milayein — roz 5 sawaal FREE!
        </p>
        <a
          href="https://wa.me/15556317048?text=Hi"
          style={styles.whatsappBtn}
          target="_blank"
          rel="noreferrer"
        >
          <MessageCircle size={18} />
          WhatsApp Par Try Karo
        </a>
        <div style={styles.footerNote}>
          StudyDost v1.0 • Let's Redefine Private Limited, Noida
        </div>
      </div>

    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#F9FAFB', overflowY: 'auto' },

  // Hero
  hero: { background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)', padding: '32px 20px 40px', textAlign: 'center' },
  heroBadge: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.9)', color: '#1E40AF', fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 20, marginBottom: 20 },
  heroTitle: { fontSize: 28, fontWeight: 900, color: '#FFFFFF', lineHeight: 1.3, marginBottom: 12 },
  heroAccent: { color: '#FDE68A' },
  heroDesc: { fontSize: 15, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, marginBottom: 24, maxWidth: 340, margin: '0 auto 24px' },
  statRow: { display: 'flex', justifyContent: 'center', gap: 12, marginTop: 24 },
  statBox: { background: 'rgba(255,255,255,0.15)', borderRadius: 12, padding: '10px 16px', textAlign: 'center' },
  statNum: { fontSize: 20, fontWeight: 800, color: '#FFFFFF' },
  statLabel: { fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 2 },

  // Register
  registerBox: { background: '#FFFFFF', margin: '20px 16px', borderRadius: 20, padding: '24px 20px', boxShadow: '0 4px 20px rgba(30,64,175,0.1)' },
  registerTitle: { fontSize: 18, fontWeight: 800, color: '#111827', marginBottom: 8, textAlign: 'center' },
  registerDesc: { fontSize: 14, color: '#6B7280', textAlign: 'center', marginBottom: 20 },
  phoneRow: { display: 'flex', border: '2px solid #DBEAFE', borderRadius: 12, overflow: 'hidden', marginBottom: 14 },
  countryCode: { background: '#F3F4F6', padding: '13px 12px', fontSize: 14, color: '#374151', borderRight: '2px solid #DBEAFE', whiteSpace: 'nowrap' },
  phoneInput: { flex: 1, border: 'none', padding: '13px 14px', fontSize: 16, outline: 'none' },
  registerBtn: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: '#1E40AF', color: '#FFFFFF', border: 'none', borderRadius: 12, padding: '14px', fontSize: 16, fontWeight: 700, cursor: 'pointer', marginBottom: 12 },
  registerNote: { textAlign: 'center', fontSize: 12, color: '#9CA3AF' },
  successBox: { textAlign: 'center', padding: '8px 0' },
  successEmoji: { fontSize: 48, marginBottom: 12 },
  successTitle: { fontSize: 20, fontWeight: 800, color: '#166534', marginBottom: 8 },
  successDesc: { fontSize: 14, color: '#374151' },

  // Sections
  section: { padding: '24px 16px' },
  sectionTitle: { fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 16, textAlign: 'center' },

  // Report preview
  reportPreview: { background: '#FFFFFF', borderRadius: 16, padding: '20px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #E5E7EB' },
  reportHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 },
  reportLogo: { fontSize: 32 },
  reportName: { fontSize: 16, fontWeight: 700, color: '#111827' },
  reportDate: { fontSize: 12, color: '#6B7280', marginTop: 2 },
  reportDivider: { height: 1, background: '#F3F4F6', margin: '16px 0' },
  reportStats: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  reportStat: { background: '#F9FAFB', borderRadius: 10, padding: '12px', textAlign: 'center' },
  reportStatIcon: { fontSize: 20, marginBottom: 4 },
  reportStatValue: { fontSize: 16, fontWeight: 800, marginBottom: 2 },
  reportStatLabel: { fontSize: 11, color: '#6B7280' },
  subjectBars: { display: 'flex', flexDirection: 'column', gap: 10 },
  barRow: { display: 'flex', alignItems: 'center', gap: 10 },
  barLabel: { width: 100, fontSize: 12, color: '#374151', flexShrink: 0 },
  barTrack: { flex: 1, height: 8, background: '#F3F4F6', borderRadius: 4, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
  barCount: { width: 24, fontSize: 12, fontWeight: 700, color: '#374151', textAlign: 'right' },
  reportFooter: { textAlign: 'center', fontSize: 11, color: '#9CA3AF', marginTop: 16 },

  // Features
  featuresGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  featureCard: { borderRadius: 14, padding: '16px', display: 'flex', flexDirection: 'column', gap: 8 },
  featureIcon: { marginBottom: 4 },
  featureTitle: { fontSize: 13, fontWeight: 700, color: '#111827' },
  featureDesc: { fontSize: 12, color: '#4B5563', lineHeight: 1.5 },

  // Steps
  steps: { display: 'flex', flexDirection: 'column', gap: 16 },
  step: { display: 'flex', alignItems: 'flex-start', gap: 16, background: '#FFFFFF', borderRadius: 14, padding: '16px' },
  stepNum: { width: 36, height: 36, borderRadius: '50%', background: '#1E40AF', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, flexShrink: 0 },
  stepTitle: { fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 4 },
  stepDesc: { fontSize: 13, color: '#6B7280', lineHeight: 1.4 },

  // FAQ
  faqCard: { background: '#FFFFFF', borderRadius: 12, marginBottom: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  faqQuestion: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: 'none', border: 'none', fontSize: 14, fontWeight: 600, color: '#111827', cursor: 'pointer', textAlign: 'left', gap: 12 },
  faqAnswer: { padding: '0 16px 14px', fontSize: 13, color: '#4B5563', lineHeight: 1.6 },

  // Footer CTA
  footerCta: { background: 'linear-gradient(135deg, #1E40AF 0%, #3B82F6 100%)', padding: '32px 20px', textAlign: 'center' },
  footerCtaEmoji: { fontSize: 48, marginBottom: 12 },
  footerCtaTitle: { fontSize: 22, fontWeight: 800, color: '#FFFFFF', marginBottom: 8 },
  footerCtaDesc: { fontSize: 14, color: 'rgba(255,255,255,0.85)', marginBottom: 24, lineHeight: 1.5 },
  whatsappBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, background: '#25D366', color: '#FFFFFF', textDecoration: 'none', padding: '14px 24px', borderRadius: 12, fontSize: 15, fontWeight: 700 },
  footerNote: { fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 24 },
};
